import { useCallback, useEffect, useRef, useState } from "react";
import {
  getNotifications,
  getUnreadCount,
  markRead as apiMarkRead,
  markAllRead as apiMarkAllRead,
} from "../services/NotificationService";
import type { Notification } from "../types/notification.types";
import { connectSocket, getSocket } from "utils/socket";
import { NOTIFICATION_PAGE_SIZE } from "../constants/notification.constants";

export function useNotifications({
  panelOpen,
  onNewNotification,
}: {
  panelOpen: boolean;
  onNewNotification?: (n: Notification) => void;
}) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // Refs so socket handlers always see current values without re-subscribing
  const panelOpenRef = useRef(panelOpen);
  useEffect(() => {
    panelOpenRef.current = panelOpen;
  }, [panelOpen]);

  const onNewNotificationRef = useRef(onNewNotification);
  useEffect(() => {
    onNewNotificationRef.current = onNewNotification;
  }, [onNewNotification]);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const res = await getUnreadCount();
      if (res.success) setUnreadCount(res.data?.count ?? 0);
    } catch {}
  }, []);

  const loadPage = useCallback(async (pageNum: number, replace: boolean) => {
    setLoading(true);
    try {
      const res = await getNotifications({
        page: pageNum,
        limit: NOTIFICATION_PAGE_SIZE,
      });
      if (res.success && res.data) {
        const rows = res.data.rows;
        setNotifications((prev) => (replace ? rows : [...prev, ...rows]));
        setPage(pageNum);
        setHasMore(pageNum < res.data.totalPages);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch badge count once on mount
  useEffect(() => {
    refreshUnreadCount();
  }, [refreshUnreadCount]);

  // Socket wiring — runs once; uses refs for stable callbacks
  useEffect(() => {
    connectSocket();
    const socket = getSocket();

    const onNotification = (data: Notification) => {
      setUnreadCount((c) => c + 1);
      if (panelOpenRef.current) {
        setNotifications((prev) => [data, ...prev]);
      }
      onNewNotificationRef.current?.(data);
    };

    const onReconnect = () => {
      // Re-fetch on reconnect — socket push is a nudge, REST is the source of truth
      refreshUnreadCount();
      if (panelOpenRef.current) loadPage(1, true);
    };

    socket.on("notification", onNotification);
    socket.io.on("reconnect", onReconnect);
    // connect_error = degraded (REST-only) mode; no crash
    socket.on("connect_error", () => {});

    return () => {
      socket.off("notification", onNotification);
      socket.io.off("reconnect", onReconnect);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load list whenever the panel opens (or first opens)
  useEffect(() => {
    if (panelOpen) loadPage(1, true);
  }, [panelOpen, loadPage]);

  const markRead = useCallback(
    async (id: number) => {
      const target = notifications.find((n) => n.id === id);
      if (target?.isRead) return;
      try {
        await apiMarkRead(id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {}
    },
    [notifications],
  );

  const markAllRead = useCallback(async () => {
    try {
      await apiMarkAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {}
  }, []);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) loadPage(page + 1, false);
  }, [loading, hasMore, page, loadPage]);

  return {
    notifications,
    unreadCount,
    loading,
    hasMore,
    markRead,
    markAllRead,
    loadMore,
  };
}
